/**
 * Developer: Hady Abdelkader (Abtra Inc)
 * Date: 2024-02-08
 * Description: This file contains the code to fetch the site reviews from the Shopper Approved API and display them on the website.
 * The code uses the Fetch API to make a GET request to the Shopper Approved API and then uses the response data to create the site reviews.
 * The site reviews are then displayed on the website using the Tiny Slider library.
 * The code also includes event listeners to handle the click events for the next and previous buttons of the slider.
 * The code is written in ES6 syntax and uses the async/await pattern to handle asynchronous operations.
 * The code also includes error handling to handle any errors that may occur during the fetch operation.
 * The code is also written in a modular and reusable way to make it easy to maintain and extend.
 */

// On browser DOM content loaded, select .reviews__widget>.row element, create a virtual fragment, add a div to it, and then append the fragment to the .row element, use the ES6 syntax.

const MAX_REVIEWS = 6;
const REVIEWS_URL = 'https://reviews.abtra.com';

// eslint-disable-next-line no-undef
const getShopperApprovedSiteReviews = async () => {
    const params = new URLSearchParams({
        // eslint-disable-next-line no-undef
        siteId: sr_values.siteId,
        page: 0,
        limit: MAX_REVIEWS,
    });
    // Get reviews from https://reviews.abtra.com/api/shopperApproved
    const response = await fetch(new URL(`/api/shopperApproved/?${params}`, REVIEWS_URL), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // eslint-disable-next-line no-undef
            'x-api-key': sr_values.apiKey,
        },
    });
    // If the response is not ok, return
    if (!response.ok)
        return;
    // Parse the response data
    const responseData = await response.json();
    // If the response data is empty, return
    if (!Object.entries(responseData).length)
        return;
    // Return the siteReviews array
    return responseData.siteReviews;
};

// eslint-disable-next-line no-undef
document.addEventListener('DOMContentLoaded', async () => {
    // Create a document fragment
    // eslint-disable-next-line no-undef
    const fragment = document.createDocumentFragment();

    // Create elements
    // eslint-disable-next-line no-undef
    const row = document.createElement('div');
    row.className = 'row';
    row.style.position = 'relative';

    // eslint-disable-next-line no-undef
    const col1 = document.createElement('div');
    let col1ClassName = 'd-flex align-items-start';
    col1.className = col1ClassName;
    // eslint-disable-next-line no-undef
    if (window.innerWidth >= 768) {
        col1ClassName = 'col ' + col1ClassName;
    } else {
        col1.style.width = '100%';
    }

    // eslint-disable-next-line no-undef
    const logoContainer = document.createElement('div');
    logoContainer.id = 'shopperapproved-logo-container';
    logoContainer.className = 'testimonial_item';

    // If the device is a mobile, position the logo container to the center
    // eslint-disable-next-line no-undef
    if (window.innerWidth < 768) {
        logoContainer.style.margin = '0 auto';
    }

    // eslint-disable-next-line no-undef
    const link = document.createElement('a');
    link.className = 'img_container';
    // eslint-disable-next-line no-undef
    link.href = sr_values.reviewsUrl;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';

    // eslint-disable-next-line no-undef
    const img = document.createElement('img');
    // eslint-disable-next-line no-undef
    img.src = `https://ab-bhs-ca-3.abtra.ovh/shopperapproved/badges/badge-${sr_values.siteId}.png`;
    img.loading = 'lazy';
    img.alt = 'Shopper Approved badge';
    img.style.width = '230px';

    // Append elements
    link.appendChild(img);
    logoContainer.appendChild(link);
    col1.appendChild(logoContainer);
    row.appendChild(col1);

    // Create the second column
    // eslint-disable-next-line no-undef
    const col2 = document.createElement('div');
    col2.className = 'col-9 col-xs-9';
    // eslint-disable-next-line no-undef
    if (window.innerWidth < 768) {
        col2.style.margin = '0 auto';
    }

    // Create the abMnext div
    // eslint-disable-next-line no-undef
    const abMnext = document.createElement('div');
    abMnext.className = 'ab-mnext';

    // eslint-disable-next-line no-undef
    const abImgnxt = document.createElement('img');
    abImgnxt.id = 'ab-imgnxt';
    abImgnxt.src = 'https://ab-bhs-ca-3.abtra.ovh/shopperapproved/public/img/slider_next.png';
    abImgnxt.alt = 'Slider Next';

    abMnext.appendChild(abImgnxt);
    col2.appendChild(abMnext);

    // Create the abMprev div
    // eslint-disable-next-line no-undef
    const abMprev = document.createElement('div');
    abMprev.className = 'ab-mprev';

    // eslint-disable-next-line no-undef
    const abImgprev = document.createElement('img');
    abImgprev.id = 'ab-imgprev';
    abImgprev.src = 'https://ab-bhs-ca-3.abtra.ovh/shopperapproved/public/img/slider_prev.png';
    abImgprev.alt = 'Slider Prev';

    abMprev.appendChild(abImgprev);
    col2.appendChild(abMprev);

    // Create the my-slider div
    // eslint-disable-next-line no-undef
    const mySlider = document.createElement('div');
    mySlider.className = 'my-slider';

    const siteReviews = (await getShopperApprovedSiteReviews()) ?? [];
    for (const siteReview of siteReviews) {
        // Create the review item
        // eslint-disable-next-line no-undef
        const reviewItem = document.createElement('div');
        reviewItem.className = 'col-12 review-item';

        // eslint-disable-next-line no-undef
        const reviewLink = document.createElement('a');
        reviewLink.style.textDecoration = 'none';
        reviewLink.style.color = 'black';
        reviewLink.href = siteReview.url;
        reviewLink.rel = 'noopener noreferrer';
        reviewLink.target = '_blank';

        // eslint-disable-next-line no-undef
        const reviewText = document.createElement('p');
        reviewText.className = 'review-text';
        let message = null;
        if (siteReview.message) {
            message = siteReview.message;
            // Clean the message from any HTML tags
            message = message.replace(/<[^>]*>?/gm, '');
        }
        if (message) {
            reviewText.innerHTML = message;
        } else {
            reviewText.textContent = 'No feedback given.';
        }

        // eslint-disable-next-line no-undef
        const reviewBottom = document.createElement('div');
        reviewBottom.className = 'review-bottom';

        // eslint-disable-next-line no-undef
        const reviewInfo = document.createElement('div');
        reviewInfo.className = 'review-info';

        // eslint-disable-next-line no-undef
        const reviewBy = document.createElement('p');
        reviewBy.className = 'review-by';
        reviewBy.textContent = siteReview.name;

        // eslint-disable-next-line no-undef
        const reviewStars = document.createElement('p');
        reviewStars.className = 'review-stars';
        reviewStars.innerHTML = 'Rating: ' + '<i></i>'.repeat(siteReview.stars);

        reviewInfo.appendChild(reviewBy);
        reviewInfo.appendChild(reviewStars);
        reviewBottom.appendChild(reviewInfo);

        // eslint-disable-next-line no-undef
        const reviewLetter = document.createElement('div');
        reviewLetter.className = 'review-letter';

        // eslint-disable-next-line no-undef
        const span = document.createElement('span');
        span.textContent = siteReview.name?.charAt(0).toUpperCase() ?? 'A';

        reviewLetter.appendChild(span);
        reviewBottom.appendChild(reviewLetter);

        reviewLink.appendChild(reviewText);
        reviewLink.appendChild(reviewBottom);
        reviewItem.appendChild(reviewLink);
        mySlider.appendChild(reviewItem);
    }

    col2.appendChild(mySlider);

    // eslint-disable-next-line no-undef
    const seeMoreLink = document.createElement('a');
    // eslint-disable-next-line no-undef
    seeMoreLink.href = sr_values.reviewsUrl;
    seeMoreLink.className = 'reviews__widget-seemore';
    seeMoreLink.rel = 'noopener noreferrer';
    seeMoreLink.target = '_blank';
    seeMoreLink.textContent = 'See more reviews »';

    col2.appendChild(seeMoreLink);

    // Append the second column to the row
    row.appendChild(col2);

    // Append row to the fragment
    fragment.appendChild(row);

    // Get the .reviews__widget element and append the fragment to it
    // eslint-disable-next-line no-undef
    const reviewsWidget = document.querySelector('.reviews__widget');
    reviewsWidget.appendChild(fragment);

    // eslint-disable-next-line no-undef
    const hr = document.createElement('hr');
    reviewsWidget.appendChild(hr);

    // Set the textContent of .reviews__widget-title to the value of sr_values.reviewsTitle
    // eslint-disable-next-line no-undef
    const reviewsWidgetTitle = document.querySelector('.reviews__widget-title');
    // eslint-disable-next-line no-undef
    reviewsWidgetTitle.textContent = sr_values.reviewsTitle;

    // Set the href of .reviews__widget-all and .reviews__widget-seemore to the value of sr.values.reviewsUrl
    // eslint-disable-next-line no-undef
    const reviewsWidgetAll = document.querySelector('.reviews__widget-all');
    // eslint-disable-next-line no-undef
    reviewsWidgetAll.href = sr_values.reviewsUrl;
    // eslint-disable-next-line no-undef
    const reviewsWidgetSeeMore = document.querySelector('.reviews__widget-seemore');
    // eslint-disable-next-line no-undef
    reviewsWidgetSeeMore.href = sr_values.reviewsUrl;

    // If the device is a mobile, set the slider items to 1
    // eslint-disable-next-line no-undef
    const sliderItems = window.innerWidth < 768 ? 1 : 3;

    // Arnel Tiny Slider
    // eslint-disable-next-line no-undef
    var slider = tns({
        'container': '.my-slider',
        'items': sliderItems,
        'speed': 2000,
        'autoplay': true,
        'autoplayButtonOutput': false,
        'autoplayHoverPause': true,
        'autoplayTimeout': 6000,
        'swipeAngle': false,
        'nav': false,
        'controls': false,
    });

    // eslint-disable-next-line no-undef
    $('div.ab-mnext').click(function () {
        slider.goTo('next');
    });

    // eslint-disable-next-line no-undef
    $('div.ab-mprev').click(function () {
        slider.goTo('prev');
    });

    //eslint-disable-next-line no-undef
    $('#shopperapproved-logo-container').on('contextmenu', function (e) {
        e.preventDefault();
        // eslint-disable-next-line no-undef
        var d = new Date(); alert('Copying Prohibited by Law - This image and all included logos are copyrighted by Shopper Approved (c) ' + d.getFullYear() + '.');
        return false;
    });
});